// Tiny HTML sanitizer for admin-authored promo blocks.
//
// We accept hand-authored HTML from the admin editor and inject it into
// the signup SPA via dangerouslySetInnerHTML. Anything an admin can
// type, an attacker who pwns an admin account can type too — so the
// sanitizer's job is to reduce the blast radius even when the source
// is "trusted".
//
// Why not DOMPurify? Adding a dependency just for one promo block
// felt heavy. The whitelist below covers the use case (formatted
// promo copy + the occasional inline link) without the kitchen sink.
// If we ever need MathML, SVG with embedded scripts, or other exotic
// surfaces, swap to DOMPurify and delete this file.
//
// Whitelist (intentionally small):
// Tags:  p, br, strong, em, b, i, u, span, div, ul, ol, li, a, h1-h6,
//        blockquote, hr, code, pre, small, sub, sup
// Attrs: href (on <a>, http/https/mailto only), title, alt, class,
//        id, target=_blank (forced rel="noopener noreferrer")
//
// Always stripped: <script>, <iframe>, <object>, <embed>, <link>,
// <style>, <meta>, <base>, <form>, <input>, <button>, every on*
// handler, every javascript:/data:/vbscript: URL, every <a target=>
// that isn't _blank.
//
// `sanitizeHtml` returns the cleaned HTML plus a list of human-
// readable warnings about what was removed, so the admin editor can
// surface them in its preview pane.

export interface SanitizeResult {
  html: string;
  warnings: string[];
}

const ALLOWED_TAGS = new Set([
  'P',
  'BR',
  'STRONG',
  'EM',
  'B',
  'I',
  'U',
  'SPAN',
  'DIV',
  'UL',
  'OL',
  'LI',
  'A',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'BLOCKQUOTE',
  'HR',
  'CODE',
  'PRE',
  'SMALL',
  'SUB',
  'SUP',
]);

const ALLOWED_ATTRS = new Set(['href', 'title', 'alt', 'class', 'id', 'target', 'rel']);

const SAFE_URL_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];

export function sanitizeHtml(input: string): SanitizeResult {
  const warnings: string[] = [];
  if (!input) return { html: '', warnings };
  // DOMParser gives us a real (inert) DOM tree we can walk; no
  // execution side-effects, no fetch of <img src>, no fragment-only
  // gotchas.
  const doc = new DOMParser().parseFromString(`<div id="__sanitize_root">${input}</div>`, 'text/html');
  const root = doc.getElementById('__sanitize_root');
  if (!root) return { html: '', warnings: ['parser-returned-no-root'] };
  walk(root, warnings);
  return { html: root.innerHTML, warnings };
}

function walk(node: Element, warnings: string[]): void {
  // Iterate over a snapshot — we mutate as we go.
  const children = Array.from(node.children);
  for (const child of children) {
    const tag = child.tagName;
    if (!ALLOWED_TAGS.has(tag)) {
      warnings.push(`removed <${tag.toLowerCase()}>`);
      child.remove();
      continue;
    }
    // Strip every attribute we don't allow; also strip every on*
    // event handler regardless of the allow list (defense in depth in
    // case ALLOWED_ATTRS ever grows accidentally).
    for (const attr of Array.from(child.attributes)) {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on')) {
        warnings.push(`stripped ${name}="${truncate(attr.value)}"`);
        child.removeAttribute(attr.name);
        continue;
      }
      if (!ALLOWED_ATTRS.has(name)) {
        warnings.push(`stripped attr ${name} on <${tag.toLowerCase()}>`);
        child.removeAttribute(attr.name);
        continue;
      }
      if (name === 'href') {
        const safe = isSafeUrl(attr.value);
        if (!safe) {
          warnings.push(`stripped unsafe href="${truncate(attr.value)}"`);
          child.removeAttribute(attr.name);
        }
      }
      if (name === 'target') {
        if (attr.value !== '_blank') {
          child.removeAttribute(attr.name);
        } else {
          // Force a safe rel; user-provided rel is ignored.
          child.setAttribute('rel', 'noopener noreferrer');
        }
      }
    }
    walk(child, warnings);
  }
}

function isSafeUrl(raw: string): boolean {
  const trimmed = raw.trim();
  if (trimmed.startsWith('#') || trimmed.startsWith('/') || trimmed.startsWith('./') || trimmed.startsWith('../')) {
    // Relative / anchor links are fine.
    return true;
  }
  try {
    const u = new URL(trimmed, 'https://example.invalid/');
    return SAFE_URL_PROTOCOLS.includes(u.protocol);
  } catch {
    return false;
  }
}

function truncate(s: string): string {
  return s.length > 40 ? s.slice(0, 37) + '...' : s;
}
